import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LoadingSpinner from '../components/LoadingSpinner';
import { API_BASE } from '../config';
import { loginSuccess } from '../slices/authSlice';
import { fetchMemberDashboardSession, navigateAfterMemberLogin } from '../utils/memberPostLogin';
import {
  getStoredMemberToken,
  handleMemberSessionFailure,
} from '../utils/memberSession';
import {
  needsProfileAddress,
  needsProfileDob,
  needsProfileMobile,
  ROSTER_DOB_PLACEHOLDER,
} from '../utils/profileCompletion';
import { parseUserDob, computeAgeFromIsoDate, dobPartsToIsoDate, isoDateToDobParts } from '../utils/parseUserDob';
import {
  captureCurrentLocationForAddress,
  geolocationErrorMessage,
} from '../utils/profileAddress';
import './Login.css';

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function formatDobForDisplay(isoDate) {
  if (!isoDate || isoDate.length < 10) return '';
  const [y, m, d] = isoDate.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

function RequiredMark() {
  return <span className="login-required"> *</span>;
}

export default function CompleteProfile() {
  const token = useSelector((state) => state.auth.token);
  const authKind = useSelector((state) => state.auth.authKind);
  const user = useSelector((state) => state.auth.user);
  const roles = useSelector((state) => state.auth.roles);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [prefetchedUser, setPrefetchedUser] = useState(null);
  const [showMobile, setShowMobile] = useState(true);
  const [showAddress, setShowAddress] = useState(true);
  const [showDob, setShowDob] = useState(false);
  const [address, setAddress] = useState('');
  const [pincode, setPincode] = useState('');
  const [mobile, setMobile] = useState('');
  const [dobDate, setDobDate] = useState('');
  const [computedAge, setComputedAge] = useState('');
  const [addressLat, setAddressLat] = useState(null);
  const [addressLng, setAddressLng] = useState(null);
  const [error, setError] = useState('');
  const [locationNotice, setLocationNotice] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [prefetchLoading, setPrefetchLoading] = useState(true);
  const [prefetchError, setPrefetchError] = useState('');
  const [prefetchTick, setPrefetchTick] = useState(0);
  const dobInputRef = useRef(null);

  const syncAgeFromDob = useCallback((iso) => {
    const age = computeAgeFromIsoDate(iso);
    setComputedAge(age != null ? String(age) : '');
  }, []);

  useEffect(() => {
    const authToken = getStoredMemberToken(token);
    if (!authToken) {
      navigate('/login', { replace: true });
      return;
    }
    if (authKind === 'ops') {
      navigate('/ops', { replace: true });
      return;
    }
    if (authKind === 'doctor') {
      navigate('/doctor/pending', { replace: true });
    }
  }, [token, authKind, navigate]);

  const retryPrefetch = useCallback(() => {
    setPrefetchTick((n) => n + 1);
  }, []);

  useEffect(() => {
    const authToken = getStoredMemberToken(token);
    if (!authToken || authKind === 'ops' || authKind === 'doctor') return;

    let cancelled = false;
    setPrefetchLoading(true);
    setPrefetchError('');

    fetchMemberDashboardSession(authToken)
      .then(({ ok, status, data }) => {
        if (cancelled) return;
        if (!ok) {
          const outcome = handleMemberSessionFailure({
            status,
            data,
            dispatch,
            navigate,
          });
          if (outcome === 'retry') {
            setPrefetchError(
              status === 503
                ? data.error || 'Unable to verify account access right now. Please try again.'
                : 'Could not load your profile. Please try again.'
            );
          }
          return;
        }

        if (!data.needsProfileCompletion) {
          navigateAfterMemberLogin(navigate, {
            needsProfileCompletion: false,
            needsMemberOnboarding: data.needsMemberOnboarding !== false,
            roles: data.roles || roles,
            authKind: 'member',
          });
          return;
        }

        const u = data.user || user;
        if (!u) return;

        setPrefetchedUser(u);
        const mobileMissing = needsProfileMobile(u);
        const addressMissing = needsProfileAddress(u);
        const dobMissing = needsProfileDob(u);
        setShowMobile(mobileMissing);
        setShowAddress(addressMissing);
        setShowDob(dobMissing);

        if (addressMissing) {
          if (u.address) setAddress(String(u.address).trim());
          if (u.pincode) setPincode(String(u.pincode).trim());
        }
        if (mobileMissing) {
          const digits = String(u.mobile || '').replace(/\D/g, '').slice(0, 10);
          if (digits) setMobile(digits);
        }
        if (dobMissing) {
          const rawDob = String(u.dob || '').trim();
          if (rawDob && rawDob !== ROSTER_DOB_PLACEHOLDER) {
            const parts = parseUserDob(u.dob);
            const iso = dobPartsToIsoDate(parts.day, parts.month, parts.year);
            setDobDate(iso);
            syncAgeFromDob(iso);
          }
        }
      })
      .finally(() => {
        if (!cancelled) setPrefetchLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, authKind, navigate, prefetchTick, dispatch, roles, user, syncAgeFromDob]);

  if (!token || authKind === 'ops' || authKind === 'doctor') {
    return null;
  }

  if (prefetchLoading) {
    return (
      <div className="proceed-page">
        <LoadingSpinner className="ops-loading" label="Loading your profile" />
      </div>
    );
  }

  if (prefetchError) {
    return (
      <div className="proceed-page">
        <div className="login-page">
          <div className="login-card-wrap">
            <div className="login-card">
              <h1 className="login-title login-title-form">Complete your profile</h1>
              <p className="login-inline-error">{prefetchError}</p>
              <button type="button" className="login-btn-submit" onClick={retryPrefetch}>
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function handleUseCurrentLocation() {
    setError('');
    setLocationNotice('');
    setIsLocating(true);
    captureCurrentLocationForAddress()
      .then(({ addressLat: lat, addressLng: lng, addressLabel }) => {
        setAddress(addressLabel);
        setAddressLat(lat);
        setAddressLng(lng);
        setLocationNotice('Location pinned. Add or confirm your 6-digit pincode below.');
      })
      .catch((err) => {
        setError(geolocationErrorMessage(err));
      })
      .finally(() => {
        setIsLocating(false);
      });
  }

  function handleAddressChange(e) {
    setAddress(e.target.value);
    setAddressLat(null);
    setAddressLng(null);
    setLocationNotice('');
  }

  function handleDobChange(e) {
    const iso = e.target.value;
    setDobDate(iso);
    syncAgeFromDob(iso);
  }

  function handleOpenDobCalendar() {
    const input = dobInputRef.current;
    if (!input) return;
    if (typeof input.showPicker === 'function') {
      input.showPicker();
    } else {
      input.click();
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    const mobileDigits = mobile.replace(/\D/g, '');
    const addressTrimmed = address.trim();
    const pincodeTrimmed = pincode.trim();

    if (showMobile && !/^\d{10}$/.test(mobileDigits)) {
      setError('Enter a valid 10-digit mobile number.');
      return;
    }
    if (showAddress) {
      if (!addressTrimmed) {
        setError('Address is required.');
        return;
      }
      if (!/^\d{6}$/.test(pincodeTrimmed)) {
        setError('Enter a valid 6-digit pincode.');
        return;
      }
    }

    if (showDob) {
      const age = computeAgeFromIsoDate(dobDate);
      if (age == null) {
        setError('Enter a valid date of birth.');
        return;
      }
      const { day, month, year } = isoDateToDobParts(dobDate);
      const formattedDob = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
      if (formattedDob === ROSTER_DOB_PLACEHOLDER) {
        setError('Please select your actual date of birth.');
        return;
      }
    }

    const savedAddress = String(prefetchedUser?.address || '').trim();
    const savedPincode = String(prefetchedUser?.pincode || '').trim();
    const finalAddress = showAddress ? addressTrimmed : savedAddress;
    const finalPincode = showAddress ? pincodeTrimmed : savedPincode;

    if (!finalAddress || !/^\d{6}$/.test(finalPincode)) {
      setError('Address and pincode are required.');
      return;
    }

    const dobParts = showDob ? isoDateToDobParts(dobDate) : null;
    const payload = {
      address: finalAddress,
      pincode: finalPincode,
      ...(showMobile ? { mobile: mobileDigits } : {}),
      ...(showDob && dobParts
        ? { day: dobParts.day, month: dobParts.month, year: dobParts.year }
        : {}),
      ...(showAddress && addressLat != null && addressLng != null
        ? { addressLat, addressLng }
        : showAddress
          ? { clearAddressCoords: true }
          : {}),
    };

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/complete-profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not save profile.');
        return;
      }

      if (data.needsProfileCompletion === true) {
        setError(
          data.rosterSyncWarning ||
            'Profile could not be completed. Please check your date of birth and try again.'
        );
        return;
      }

      dispatch(
        loginSuccess({
          token,
          user: data.user || user,
          roles,
          authKind: 'member',
        })
      );
      navigateAfterMemberLogin(navigate, {
        needsProfileCompletion: data.needsProfileCompletion === true,
        needsMemberOnboarding: data.needsMemberOnboarding !== false,
        roles,
        authKind: 'member',
      });
    } catch {
      setError('Server error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="proceed-page">
      <div className="proceed-bg-shapes" aria-hidden="true">
        <span className="proceed-shape proceed-shape-1" />
        <span className="proceed-shape proceed-shape-2" />
      </div>

      <Link to="/" className="proceed-logo">
        <img src="/images/unaffectedLogo.png" alt="" className="proceed-logo-image" />
        <span className="proceed-logo-text">Unaffected Healthcare</span>
      </Link>

      <div className="login-page">
        <div className="login-card-wrap">
          <div className="login-card">
            <h1 className="login-title login-title-form">Complete your profile</h1>
            <p className="login-subtitle">
              {showDob && !showMobile && !showAddress
                ? 'Step 1 of 2 — add your date of birth to continue.'
                : 'Step 1 of 2 — add your contact number and delivery address for medicine and lab services.'}
            </p>

            <form onSubmit={handleSubmit} className="login-form">
              {showMobile && (
                <div className="form-group">
                  <label htmlFor="complete-mobile">
                    Mobile number
                    <RequiredMark />
                  </label>
                  <input
                    id="complete-mobile"
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="10-digit mobile"
                    className="login-input"
                    autoComplete="tel"
                    required
                  />
                </div>
              )}
              {showDob && (
                <>
                  <div className="form-group">
                    <label htmlFor="complete-dob">
                      Date of birth
                      <RequiredMark />
                    </label>
                    <div className="login-dob-date-wrap">
                      <button
                        type="button"
                        className={`login-dob-date-display${!dobDate ? ' login-dob-date-display--placeholder' : ''}`}
                        onClick={handleOpenDobCalendar}
                        aria-label="Choose date of birth"
                      >
                        {dobDate ? formatDobForDisplay(dobDate) : 'DD/MM/YYYY'}
                      </button>
                      <input
                        ref={dobInputRef}
                        id="complete-dob"
                        type="date"
                        className="login-dob-date-input login-dob-date-input--hidden"
                        value={dobDate}
                        onChange={handleDobChange}
                        max={todayIsoDate()}
                        min="1900-01-01"
                        required
                        aria-hidden="true"
                        tabIndex={-1}
                      />
                      <button
                        type="button"
                        className="login-dob-date-calendar-btn"
                        onClick={handleOpenDobCalendar}
                        aria-label="Open calendar"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </button>
                    </div>
                    <p className="login-dob-hint">Tap the date or calendar icon to pick your birth date</p>
                  </div>
                  <div className="form-group">
                    <label htmlFor="complete-age">Age</label>
                    <input
                      id="complete-age"
                      type="text"
                      readOnly
                      className="login-input login-age-display"
                      value={computedAge ? `${computedAge} years` : '—'}
                      aria-readonly="true"
                      title="Calculated automatically from date of birth"
                    />
                  </div>
                </>
              )}
              {showAddress && (
                <>
                  <button
                    type="button"
                    className="login-btn-back"
                    style={{ width: '100%', marginBottom: '1rem' }}
                    onClick={handleUseCurrentLocation}
                    disabled={isLocating || isSubmitting}
                  >
                    {isLocating ? 'Getting location…' : 'Use current location'}
                  </button>
                  {locationNotice && (
                    <p
                      className="login-inline-success"
                      role="status"
                      style={{ marginBottom: '0.75rem' }}
                    >
                      {locationNotice}
                    </p>
                  )}
                  <div className="form-group">
                    <label htmlFor="complete-address">
                      Address
                      <RequiredMark />
                    </label>
                    <input
                      id="complete-address"
                      type="text"
                      value={address}
                      onChange={handleAddressChange}
                      placeholder="Enter your address"
                      className="login-input"
                      autoComplete="street-address"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="complete-pincode">
                      Pincode
                      <RequiredMark />
                    </label>
                    <input
                      id="complete-pincode"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={pincode}
                      onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="6-digit pincode"
                      className="login-input"
                      required
                    />
                  </div>
                </>
              )}
              {error && <p className="login-inline-error">{error}</p>}
              <button type="submit" className="login-btn-submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving…' : 'Continue'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
